(function () {
  const dataEl = document.getElementById("gtm-page-data");
  if (!dataEl) return;

  /** @type {any} */
  let pageData;
  try {
    pageData = JSON.parse(dataEl.textContent || "");
  } catch {
    return;
  }

  const { pageType, currency } = pageData;

  // Cast window once to avoid repeated type suppressions
  const win = /** @type {any} */ (window);
  win.dataLayer = win.dataLayer || [];

  // Clear previous ecommerce object before each push, per GA4 spec
  /** @param {Record<string, unknown>} obj */
  function push(obj) {
    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push(obj);
  }

  // Build items array from product card data attributes (collection + search pages)
  /**
   * @param {string} listName
   * @param {string} listId
   * @returns {any[]}
   */
  function getCardItems(listName, listId) {
    /** @type {any[]} */
    const items = [];
    document
      .querySelectorAll("product-card[data-gtm-item]")
      .forEach((card, index) => {
        try {
          const el = /** @type {HTMLElement} */ (card);
          const item = JSON.parse(el.dataset.gtmItem || "");
          items.push({
            ...item,
            index: index + 1,
            item_list_name: listName,
            item_list_id: listId,
          });
        } catch {}
      });
    return items;
  }

  // view_item
  if (pageType === "product" && pageData.product) {
    push({
      event: "view_item",
      ecommerce: {
        currency,
        value: pageData.product.price,
        items: [pageData.product],
      },
    });
  }

  // view_cart
  if (pageType === "cart" && pageData.items) {
    push({
      event: "view_cart",
      ecommerce: { currency, value: pageData.value, items: pageData.items },
    });
  }

  // view_item_list (collection + search)
  if (pageType === "collection" || pageType === "search") {
    const listName = pageData.listName || "";
    const listId = String(pageData.listId || "");
    const items = getCardItems(listName, listId);
    if (items.length) {
      push({
        event: "view_item_list",
        ecommerce: {
          currency,
          item_list_name: listName,
          item_list_id: listId,
          items,
        },
      });
    }
  }

  // search
  if (pageType === "search" && pageData.searchQuery) {
    push({ event: "search", search_term: pageData.searchQuery });
  }

  // select_item — click on a product card link
  document.addEventListener("click", function (e) {
    const target = /** @type {Element | null} */ (e.target);
    if (!target) return;
    const card = /** @type {HTMLElement | null} */ (
      target.closest("product-card[data-gtm-item]")
    );
    if (!card) return;
    if (!target.closest("a")) return;

    let item;
    try {
      item = JSON.parse(card.dataset.gtmItem || "");
    } catch {
      return;
    }

    const listName = pageData.listName || "";
    const listId = String(pageData.listId || "");
    push({
      event: "select_item",
      ecommerce: {
        currency,
        item_list_name: listName,
        item_list_id: listId,
        items: [item],
      },
    });
  });

  // add_to_cart — hooks into CartAddEvent which bubbles as 'cart:update'
  //
  // Lookup order:
  //   1. variantMap (PDP) — keyed by variantId, includes exact variant title/price
  //   2. productCardMap (collection quick-add) — keyed by productId from product-card elements.
  //      The original product-card stays in the DOM even after quick-add moves the form into
  //      its modal, so this lookup works regardless of where the form is rendered.
  const variantMap = /** @type {Record<string, any>} */ (
    pageData.variants || {}
  );

  /** @type {Record<string, any>} */
  const productCardMap = {};
  document
    .querySelectorAll("product-card[data-product-id][data-gtm-item]")
    .forEach((card) => {
      try {
        const el = /** @type {HTMLElement} */ (card);
        productCardMap[el.dataset.productId || ""] = JSON.parse(
          el.dataset.gtmItem || "",
        );
      } catch {}
    });

  // Build a GA4 item from a Shopify AJAX cart line item (has vendor + product_type).
  /** @param {any} line */
  function itemFromCartLine(line) {
    return {
      item_id: String(line.variant_id ?? ""),
      item_name: line.product_title ?? line.title ?? "",
      item_brand: line.vendor ?? "",
      item_category: line.product_type ?? "",
      item_variant: line.variant_title ?? "",
      price: (line.final_price ?? line.price ?? 0) / 100,
      quantity: line.quantity ?? 1,
      gender: "Women",
    };
  }

  document.addEventListener("cart:update", function (e) {
    const ce = /** @type {CustomEvent} */ (e);
    if (!ce.detail?.data) return;
    const { source, didError, itemCount, productId } = ce.detail.data;
    if (source !== "product-form-component" || didError) return;

    // Quick-add paths dispatch the full /cart/add.js response; their sourceId is a
    // static label (e.g. "quick-buy-sizes"), not a variant id, so build items
    // straight from the response — exact for the added size and correct for
    // related-product quick-adds (which aren't in productCardMap).
    const resource = ce.detail.resource;
    if (resource && Array.isArray(resource.items) && resource.items.length) {
      const items = resource.items.map(itemFromCartLine);
      const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
      push({ event: "add_to_cart", ecommerce: { currency, value, items } });
      return;
    }

    // PDP form path — resource is empty and sourceId IS the added variant id.
    const variantId = /** @type {string} */ (String(ce.detail.sourceId || ""));
    const quantity = /** @type {number} */ (itemCount || 1);
    const fromVariantMap = variantId in variantMap;
    const item =
      (fromVariantMap ? variantMap[variantId] : null) ||
      productCardMap[productId];
    if (!item) return;

    push({
      event: "add_to_cart",
      ecommerce: {
        currency,
        value: /** @type {number} */ (item.price) * quantity,
        items: [
          {
            ...item,
            ...(fromVariantMap ? { item_id: variantId } : {}),
            quantity,
          },
        ],
      },
    });
  });

  // sign_up — Klaviyo forms dispatch a 'klaviyoForms' event on successful submission
  document.addEventListener("klaviyoForms", function (e) {
    const ce = /** @type {CustomEvent} */ (e);
    if (ce.detail?.type !== "submit") return;
    push({ event: "sign_up", method: "email" });
  });
})();
